/**
 * Pure helpers for the character-build panels. Everything here is DOM-free so it
 * can be unit tested in Node; rules themselves live in `src/game/*` and are only
 * called (never re-derived) except for a few display-only mirrors marked below.
 */
import { CARDS, CARD_SETS, type CardCollection, type CardDef, MAX_AWAKEN, SIGILS, type SigilDef, addCard, awakenCost, sigilLevel, stoneSigilPoints } from '../../game/build';
import type { BuiltCharacter, CharacterSave } from '../../game/character';
import { countOf, itemValue, removeStack } from '../../game/inventory';
import {
  ACC_SLOTS,
  type BattleCategory,
  type CombatStat,
  type EquipItem,
  type GearItem,
  type ItemDef,
  type ItemKind,
  ITEMS,
  type Rarity,
  gearIlvl,
  rarityIndex,
} from '../../game/items';
import { hasLootTable, rollLoot } from '../../game/loot';
import type { Progression } from '../../game/Progression';
import type { TemperOutcome } from '../../game/tempering';
import { passiveHooks } from '../../sim/passives';
import { effectiveCooldown, levelMult, resolveSkill } from '../../sim/skills';
import type { ResolvedSkill, SkillAction, SkillDef } from '../../sim/skillTypes';
import type { CastType, StaggerTier, SuperArmor } from '../../sim/types';

// ====================================================================== skill bar

export const BAR_KEYS = ['Q', 'W', 'E', 'R', 'A', 'S', 'D', 'F'] as const;
export const BAR_SLOTS = BAR_KEYS.length;

/** Pads/truncates a bar to 8 slots and drops duplicate skills (first occurrence wins). */
export function normalizeBar(bar: readonly (string | null | undefined)[], size = BAR_SLOTS): (string | null)[] {
  const out: (string | null)[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < size; i++) {
    const id = bar[i] ?? null;
    if (id && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    } else out.push(null);
  }
  return out;
}

/**
 * Puts a skill into a bar slot. If the skill already sits in another slot the two
 * slots swap, so the bar never holds a skill twice and never loses a slot.
 */
export function assignToBar(bar: readonly (string | null)[], skillId: string, slot: number): (string | null)[] {
  const next = normalizeBar(bar);
  if (slot < 0 || slot >= next.length) return next;
  const from = next.indexOf(skillId);
  if (from === slot) return next;
  if (from >= 0) next[from] = next[slot] ?? null;
  next[slot] = skillId;
  return next;
}

export function swapBarSlots(bar: readonly (string | null)[], a: number, b: number): (string | null)[] {
  const next = normalizeBar(bar);
  if (a < 0 || b < 0 || a >= next.length || b >= next.length || a === b) return next;
  const t = next[a] ?? null;
  next[a] = next[b] ?? null;
  next[b] = t;
  return next;
}

export function clearBarSlot(bar: readonly (string | null)[], slot: number): (string | null)[] {
  const next = normalizeBar(bar);
  if (slot >= 0 && slot < next.length) next[slot] = null;
  return next;
}

// ====================================================================== battle items

export const BATTLE_SLOT_COUNT = 4;

export function battleCategoryOf(id: string | null | undefined): BattleCategory | null {
  if (!id) return null;
  const d = ITEMS[id];
  return d && d.kind === 'battle' && d.battle ? d.battle.category : null;
}

/**
 * Assigns a battle item to one of the four battle slots. Only `battle` items are
 * accepted and each category (potion / grenade / bomb / utility) may appear once:
 * an item of an already-slotted category replaces it (or takes its slot when no
 * target is given). Moving an item that is already slotted swaps the two slots.
 * Returns null when the item cannot go into a battle slot.
 */
export function assignBattleSlot(slots: readonly (string | null)[], id: string, target?: number): (string | null)[] | null {
  const cat = battleCategoryOf(id);
  if (!cat) return null;
  const next: (string | null)[] = Array.from({ length: BATTLE_SLOT_COUNT }, (_, i) => slots[i] ?? null);
  const existing = next.indexOf(id);
  if (target === undefined) {
    if (existing >= 0) return next;
    const conflict = next.findIndex((s) => battleCategoryOf(s) === cat);
    const empty = next.findIndex((s) => !s);
    target = conflict >= 0 ? conflict : empty >= 0 ? empty : 0;
  }
  if (target < 0 || target >= BATTLE_SLOT_COUNT) return null;
  if (existing >= 0) {
    if (existing !== target) {
      next[existing] = next[target] ?? null;
      next[target] = id;
    }
    return next;
  }
  for (let i = 0; i < next.length; i++) if (i !== target && battleCategoryOf(next[i]) === cat) next[i] = null;
  next[target] = id;
  return next;
}

export function clearBattleSlot(slots: readonly (string | null)[], i: number): (string | null)[] {
  const next: (string | null)[] = Array.from({ length: BATTLE_SLOT_COUNT }, (_, k) => slots[k] ?? null);
  if (i >= 0 && i < next.length) next[i] = null;
  return next;
}

// ====================================================================== inventory

export type InvTab = 'all' | 'equipment' | 'consumables' | 'materials' | 'books';
export const INV_TABS: readonly { id: InvTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'consumables', label: 'Consumables' },
  { id: 'materials', label: 'Materials' },
  { id: 'books', label: 'Books & Gems' },
];

export type InvEntry =
  | { key: string; type: 'stack'; id: string; def: ItemDef; count: number; rarity: Rarity }
  | { key: string; type: 'equip'; item: EquipItem; rarity: Rarity };

/** Bag contents in stored order: unique equipment first, then stacks. Unknown ids are skipped. */
export function inventoryEntries(c: CharacterSave): InvEntry[] {
  const out: InvEntry[] = [];
  for (const item of c.inventory.items) out.push({ key: item.uid, type: 'equip', item, rarity: item.rarity });
  for (const [id, count] of Object.entries(c.inventory.stacks)) {
    const def = ITEMS[id];
    if (!def || count <= 0) continue;
    out.push({ key: `s:${id}`, type: 'stack', id, def, count, rarity: def.rarity });
  }
  return out;
}

const KIND_TAB: Record<ItemKind, Exclude<InvTab, 'all'>> = {
  material: 'materials',
  token: 'materials',
  junk: 'materials',
  tool: 'materials',
  quest: 'materials',
  consumable: 'consumables',
  battle: 'consumables',
  skillpotion: 'consumables',
  food: 'consumables',
  chest: 'consumables',
  book: 'books',
  gem: 'books',
  card: 'books',
};

export function entryTab(e: InvEntry): Exclude<InvTab, 'all'> {
  return e.type === 'equip' ? 'equipment' : KIND_TAB[e.def.kind];
}

export function filterEntries(list: readonly InvEntry[], tab: InvTab): InvEntry[] {
  return tab === 'all' ? [...list] : list.filter((e) => entryTab(e) === tab);
}

const EQUIP_ORDER = ['weapon', 'head', 'shoulders', 'chest', 'pants', 'gloves', 'necklace', 'earring', 'ring', 'stone'];
const KIND_ORDER: ItemKind[] = ['battle', 'consumable', 'skillpotion', 'food', 'chest', 'book', 'gem', 'card', 'material', 'token', 'tool', 'quest', 'junk'];
const TAB_ORDER: Exclude<InvTab, 'all'>[] = ['equipment', 'consumables', 'books', 'materials'];

function entryLevel(e: InvEntry): number {
  if (e.type === 'stack') return e.def.level ?? 0;
  const it = e.item;
  return it.kind === 'gear' ? gearIlvl(it) : it.kind === 'accessory' ? it.ilvl : 0;
}

function entryName(e: InvEntry): string {
  return e.type === 'stack' ? e.def.name : e.item.name;
}

/** Canonical bag order: tab, then equipment slot / item kind, rarity (high first), level (high first), name. */
export function compareEntries(a: InvEntry, b: InvEntry): number {
  const ta = TAB_ORDER.indexOf(entryTab(a));
  const tb = TAB_ORDER.indexOf(entryTab(b));
  if (ta !== tb) return ta - tb;
  const ka = a.type === 'equip' ? EQUIP_ORDER.indexOf(a.item.kind === 'gear' ? a.item.slot : a.item.kind === 'stone' ? 'stone' : a.item.slot) : KIND_ORDER.indexOf(a.def.kind);
  const kb = b.type === 'equip' ? EQUIP_ORDER.indexOf(b.item.kind === 'gear' ? b.item.slot : b.item.kind === 'stone' ? 'stone' : b.item.slot) : KIND_ORDER.indexOf(b.def.kind);
  if (ka !== kb) return ka - kb;
  const ra = rarityIndex(a.rarity);
  const rb = rarityIndex(b.rarity);
  if (ra !== rb) return rb - ra;
  const la = entryLevel(a);
  const lb = entryLevel(b);
  if (la !== lb) return lb - la;
  return entryName(a).localeCompare(entryName(b));
}

/** Sorts the bag in place (the order is persisted with the save). */
export function sortInventory(c: CharacterSave): void {
  const entries = inventoryEntries(c).sort(compareEntries);
  c.inventory.items = entries.flatMap((e) => (e.type === 'equip' ? [e.item] : []));
  const stacks: Record<string, number> = {};
  for (const e of entries) if (e.type === 'stack') stacks[e.id] = e.count;
  // Keep unknown/zero entries untouched so nothing is ever lost by sorting.
  for (const [id, n] of Object.entries(c.inventory.stacks)) if (!(id in stacks)) stacks[id] = n;
  c.inventory.stacks = stacks;
}

// ====================================================================== items: labels & values

/** Quality bar colour (research 03 §10.2). */
export function qualityColor(q: number): string {
  if (q >= 100) return '#fe9600';
  if (q >= 90) return '#ce43fc';
  if (q >= 70) return '#00b5ff';
  if (q >= 30) return '#91fe02';
  if (q >= 10) return '#ffd200';
  return '#ff6000';
}

/** Display tier from item level: levelling gear is Tier 1, endgame sets Tier 2–4. */
export function gearTier(ilvl: number): number {
  if (ilvl < 250) return 1;
  if (ilvl < 300) return 2;
  if (ilvl < 340) return 3;
  return 4;
}

export const RARITY_NAME: Record<Rarity, string> = {
  normal: 'Normal',
  uncommon: 'Uncommon',
  rare: 'Rare',
  epic: 'Epic',
  legendary: 'Legendary',
  relic: 'Relic',
  ancient: 'Ancient',
  mythic: 'Mythic',
};

export const SLOT_NAME: Record<string, string> = {
  weapon: 'Weapon',
  head: 'Head',
  shoulders: 'Shoulders',
  chest: 'Chest',
  pants: 'Pants',
  gloves: 'Gloves',
  necklace: 'Necklace',
  earring: 'Earring',
  earring1: 'Earring',
  earring2: 'Earring',
  ring: 'Ring',
  ring1: 'Ring',
  ring2: 'Ring',
  stone: 'Rune Stone',
};

export function equipTypeLabel(item: EquipItem): string {
  if (item.kind === 'stone') return 'Rune Stone';
  return SLOT_NAME[item.slot] ?? item.slot;
}

const KIND_NAME: Record<ItemKind, string> = {
  material: 'Material',
  consumable: 'Consumable',
  battle: 'Battle Item',
  book: 'Sigil Codex',
  skillpotion: 'Consumable',
  gem: 'Gem',
  card: 'Card',
  chest: 'Chest',
  quest: 'Quest Item',
  token: 'Currency',
  food: 'Food',
  junk: 'Trade Good',
  tool: 'Tool',
};

export function stackTypeLabel(def: ItemDef): string {
  if (def.kind === 'battle' && def.battle) return `Battle Item · ${def.battle.category[0]!.toUpperCase()}${def.battle.category.slice(1)}`;
  return KIND_NAME[def.kind];
}

/** Item level used for display (gear includes tempering). */
export function equipIlvl(item: EquipItem): number | null {
  if (item.kind === 'gear') return gearIlvl(item);
  if (item.kind === 'accessory') return item.ilvl;
  return null;
}

export function sellPrice(e: InvEntry): number {
  return e.type === 'equip' ? itemValue(e.item) : e.def.value * e.count;
}

export function canSell(e: InvEntry): boolean {
  if (e.type === 'equip') return true;
  return e.def.value > 0 && e.def.kind !== 'quest';
}

export function canSalvage(e: InvEntry): boolean {
  return e.type === 'equip' && e.item.kind !== 'stone';
}

// ====================================================================== stats

const pct = (v: number, digits = 1): string => `${(v * 100).toFixed(digits)}%`;

/**
 * What a combat stat does at a value. Display-only mirror of the divisors in
 * `buildCharacter` (src/game/character.ts) — keep in sync if those change.
 */
export function combatStatEffect(stat: CombatStat, v: number, long = false): string {
  switch (stat) {
    case 'crit':
      return `Crit rate +${pct(v / 2800)}`;
    case 'spec':
      return long ? `Apex Art & Resonance skill damage +${pct(v / 2000)}, Resonance gain +${pct(v / 4000)}` : `Apex & Resonance dmg +${pct(v / 2000)}`;
    case 'swift':
      return long ? `Attack & move speed +${pct(v / 5800)}, cooldowns −${pct(v / 4700)}` : `Speed +${pct(v / 5800)} · Cooldowns −${pct(v / 4700)}`;
    case 'dom':
      return long ? `Damage to crowd-controlled foes +${pct(v / 2800)}` : `Dmg to controlled foes +${pct(v / 2800)}`;
    case 'endur':
      return `Damage taken −${pct(v / 9000)}`;
    case 'expert':
      return `Healing received +${pct(v / 3000)}`;
  }
}

/** A single headline number for the character sheet (display only; not used by combat). */
export function combatPower(b: BuiltCharacter): number {
  const s = b.base;
  const crit = Math.min(1, Math.max(0, s.critRate));
  const offense = s.attack * (1 + crit * Math.max(0, s.critDmg - 1)) * (1 + s.dmgBonus) * (1 + Math.min(0.6, s.cdr)) * Math.max(0.5, s.atkSpeed);
  return Math.round(offense / 4 + s.maxHp / 40);
}

// ====================================================================== skills

export const CAST_TYPE_LABEL: Record<CastType, string> = {
  normal: 'Normal',
  combo: 'Combo',
  chain: 'Chain',
  holding: 'Holding',
  charge: 'Charge',
  point: 'Point',
  casting: 'Casting',
  toggle: 'Toggle',
};

/** Cast-type label colours (03b §B Skills). */
export const CAST_TYPE_COLOR: Record<CastType, string> = {
  normal: '#f2d15c',
  combo: '#4fe3ec',
  chain: '#5ab8ff',
  holding: '#7fdc5e',
  charge: '#5ab8ff',
  point: '#f2d15c',
  casting: '#ff7fcf',
  toggle: '#c9ced4',
};

/** Facet tier colours I / II / III. */
export const TIER_COLOR = ['#559ed6', '#64c53a', '#e7c669'] as const;
export const TIER_NUMERAL = ['I', 'II', 'III'] as const;

export const STAGGER_LABEL: Record<StaggerTier, string> = {
  none: 'None',
  low: 'Low',
  mid: 'Mid',
  midHigh: 'Mid-High',
  high: 'High',
  max: 'Very High',
};

export const SUPER_ARMOR_LABEL: Record<SuperArmor, string> = {
  none: 'None',
  paralysis: 'Paralysis Immunity',
  push: 'Push Immunity',
  full: 'Full Immunity',
};

export const POSITIONAL_LABEL = { none: '', back: 'Back Attack', head: 'Head Attack', both: 'Back & Head Attack' } as const;

/**
 * Level at which un-levelled skills (identity, basic, dash, apex) resolve — mirrors
 * `resolvePlayerSkills` in src/sim/factory.ts.
 */
export const UNLEVELLED_LEVEL = 10;

/** Only regular class skills take skill points and levels. */
export const isLevelled = (def: SkillDef): boolean => !def.kind || def.kind === 'skill';

/** The level a character's skill resolves at. */
export const skillLevelOf = (c: CharacterSave, def: SkillDef): number => (isLevelled(def) ? (c.skillLevels[def.id] ?? 1) : UNLEVELLED_LEVEL);

function areaTicks(duration = 0, interval = 0): number {
  return duration > 0 && interval > 0 ? 1 + Math.floor(duration / interval + 1e-6) : 1;
}

function actionCoef(a: SkillAction): number {
  switch (a.kind) {
    case 'hit':
      return a.hit.friendly ? 0 : a.hit.coef;
    case 'projectile': {
      const p = a.proj;
      const per = (p.hit.friendly ? 0 : p.hit.coef) * (p.returns ? 2 : 1) + (p.explode && !p.explode.friendly ? p.explode.coef : 0);
      return per * (p.count ?? 1);
    }
    case 'area':
      return a.area.hit.friendly ? 0 : a.area.hit.coef * areaTicks(a.area.duration, a.area.tickInterval);
    default:
      return 0;
  }
}

const sumActions = (acts: readonly SkillAction[]): number => acts.reduce((n, a) => n + actionCoef(a), 0);

/**
 * Total damage coefficient of a resolved skill (multiples of attack power,
 * before level scaling). Holding and charge skills also report their peak
 * (Perfect Zone release / overcharge).
 */
export function rawCoef(s: ResolvedSkill): { base: number; peak?: { label: string; coef: number } } {
  const pre = s.preStage ? sumActions(s.preStage.actions) : 0;
  const stages = s.stages.reduce((n, st) => n + sumActions(st.actions), 0);
  if (s.castType === 'holding' && s.holding) {
    const h = s.holding;
    const ticks = Math.max(1, Math.ceil(h.maxHold / Math.max(0.01, h.tickInterval) - 1e-6));
    const tick = sumActions(h.tick) * ticks;
    const fin = h.finisher ? sumActions(h.finisher.actions) : 0;
    const base = pre + stages + tick + fin;
    if (h.perfectZone && fin > 0 && (h.perfectMult ?? 1) !== 1) return { base, peak: { label: 'Perfect Zone', coef: pre + stages + tick + fin * (h.perfectMult ?? 1) } };
    return { base };
  }
  if (s.castType === 'charge' && s.charge) {
    const ch = s.charge;
    const full = ch.mults[ch.mults.length - 1] ?? 1;
    const base = pre + stages * full;
    if (ch.overcharge && ch.overMult) return { base, peak: { label: 'Overcharge', coef: pre + stages * ch.overMult } };
    return { base };
  }
  return { base: pre + stages };
}

export interface SkillCalc {
  resolved: ResolvedSkill;
  level: number;
  /** Cooldown after facets, gems, passives and cooldown reduction. */
  cooldown: number;
  mana: number;
  /** Damage as a fraction of attack power at the current level (1 = 100%). */
  coef: number;
  damage: number;
  peak?: { label: string; coef: number; damage: number };
}

/** Computes tooltip numbers for a skill at a level with facets, using the character's build. */
export function skillCalc(def: SkillDef, level: number, tripods: readonly (string | null)[], built: BuiltCharacter | null): SkillCalc {
  const s = resolveSkill(def, level, tripods);
  const mods = built?.skillMods[def.id];
  if (mods?.dmg) s.dmgMult *= 1 + mods.dmg;
  if (mods?.cd) s.cooldown *= 1 - mods.cd;
  for (const ref of built?.passives ?? []) {
    const h = passiveHooks(ref);
    if (h?.cooldownMult) s.cooldown *= h.cooldownMult(s);
  }
  const base = built?.base;
  const scale = levelMult(s.level) * s.dmgMult;
  const spec = s.kind === 'awakening' || s.kind === 'identity' ? 1 + (base?.specBonus ?? 0) : 1;
  const perAtk = (base?.attack ?? 0) * (1 + (base?.dmgBonus ?? 0)) * spec;
  const raw = rawCoef(s);
  const coef = raw.base * scale;
  return {
    resolved: s,
    level,
    cooldown: effectiveCooldown(s, base?.cdr ?? 0),
    mana: s.mana,
    coef,
    damage: coef * perAtk,
    peak: raw.peak ? { label: raw.peak.label, coef: raw.peak.coef * scale, damage: raw.peak.coef * scale * perAtk } : undefined,
  };
}

// ====================================================================== consumables

/** Rift Energy is capped at one day's worth (two runs). */
export const RIFT_ENERGY_CAP = 100;

export interface UseOutcome {
  ok: boolean;
  text: string;
  color?: string;
  /** Set when the item wants to move the player (the UI performs the travel). */
  travel?: { zone: string; spawn: string };
}

/**
 * Uses one stackable item from the bag. Rules come from the game layer
 * (`readSigilBook`, loot tables, card collection); simple `use` effects declared
 * on the item (`skillPoints:N`, `riftEnergy:N`, `returnHome`) are applied here.
 */
export function useStack(prog: Progression, id: string, env: { canTravel?: (zone: string) => boolean } = {}): UseOutcome {
  const def = ITEMS[id];
  const c = prog.char;
  if (!def || countOf(c, id) <= 0) return { ok: false, text: 'You do not have that item.', color: '#e5484d' };
  const done = (text: string, color = '#7bd66b'): UseOutcome => {
    prog.questEvent({ type: 'use', target: id });
    return { ok: true, text, color };
  };
  if (def.kind === 'book' && def.ref) {
    const before = c.sigilBooks[def.ref] ?? 0;
    const name = SIGILS[def.ref]?.name ?? def.ref;
    if (before >= 20) return { ok: false, text: `You have already mastered ${name} (20/20).`, color: '#e5a24d' };
    if (!prog.readSigilBook(id)) return { ok: false, text: 'The codex could not be read.', color: '#e5484d' };
    return done(`Sigil learned: ${name} ${c.sigilBooks[def.ref] ?? 0}/20`, '#c9a2ff');
  }
  const [op, arg] = (def.use ?? '').split(':');
  const n = Number(arg ?? 0);
  if (op === 'skillPoints' && n > 0) {
    if (!removeStack(c, id, 1)) return { ok: false, text: 'You do not have that item.', color: '#e5484d' };
    c.bonusSkillPoints += n;
    prog.emit('statsChanged', {});
    return done(`+${n} skill points`, '#ffd070');
  }
  if (op === 'riftEnergy' && n > 0) {
    if (c.daily.riftEnergy >= RIFT_ENERGY_CAP) return { ok: false, text: 'Rift Energy is already full.', color: '#e5a24d' };
    if (!removeStack(c, id, 1)) return { ok: false, text: 'You do not have that item.', color: '#e5484d' };
    c.daily.riftEnergy = Math.min(RIFT_ENERGY_CAP, c.daily.riftEnergy + n);
    return done(`Rift Energy restored (${c.daily.riftEnergy}/${RIFT_ENERGY_CAP})`, '#d070ff');
  }
  if (op === 'returnHome') {
    const zone = 'havenreach';
    if (env.canTravel && !env.canTravel(zone)) return { ok: false, text: 'The refrain finds no way home from here.', color: '#e5a24d' };
    if (!removeStack(c, id, 1)) return { ok: false, text: 'You do not have that item.', color: '#e5484d' };
    return { ...done('The Homeward Refrain carries you back…', '#9ad0ff'), travel: { zone, spawn: 'start' } };
  }
  if (def.kind === 'chest' && def.ref && hasLootTable(def.ref)) {
    if (!removeStack(c, id, 1)) return { ok: false, text: 'You do not have that item.', color: '#e5484d' };
    prog.grantDrop(rollLoot(def.ref, { level: c.level, rng: prog.rng }), def.name);
    return done(`Opened ${def.name}`);
  }
  if (def.kind === 'card' && def.ref && CARDS[def.ref]) {
    if (!removeStack(c, id, 1)) return { ok: false, text: 'You do not have that item.', color: '#e5484d' };
    const r = addCard(prog.account.cards, def.ref);
    prog.emit('statsChanged', {});
    return done(`${r === 'new' ? 'New card' : 'Card duplicate'}: ${CARDS[def.ref]?.name ?? def.ref}`, '#ffd070');
  }
  if (def.kind === 'battle') return { ok: false, text: 'Battle items are used from the battle item slots (1–4).', color: '#e5a24d' };
  if (def.kind === 'gem') return { ok: false, text: 'Socket gems in the Gems tab of the Sigils window (N).', color: '#e5a24d' };
  return { ok: false, text: 'This item cannot be used.', color: '#e5a24d' };
}

/** The primary action of a bag entry (right-click / double-click). */
export type EntryAction = 'equip' | 'use' | 'read' | 'assign' | 'socket' | 'none';

export function primaryAction(e: InvEntry): EntryAction {
  if (e.type === 'equip') return 'equip';
  const d = e.def;
  if (d.kind === 'book') return 'read';
  if (d.kind === 'battle') return 'assign';
  if (d.kind === 'gem') return 'socket';
  if (d.use || (d.kind === 'chest' && d.ref) || (d.kind === 'card' && d.ref)) return 'use';
  return 'none';
}

// ====================================================================== tempering

export interface TemperRun {
  attempts: TemperOutcome[];
  success: boolean;
  /** Why the run stopped. */
  reason: 'success' | 'unaffordable' | 'limit' | 'maxed';
  fromPlus: number;
  fromIlvl: number;
  fromResolve: number;
}

/** Tempers up to `times` attempts, stopping on the first success or when materials run out. */
export function temperRepeat(prog: Progression, g: GearItem, times: number, catalysts = 0, tome = false): TemperRun {
  const run: TemperRun = { attempts: [], success: false, reason: 'limit', fromPlus: g.plus, fromIlvl: gearIlvl(g), fromResolve: g.resolve };
  for (let i = 0; i < times; i++) {
    if (g.plus >= g.maxPlus) {
      run.reason = 'maxed';
      break;
    }
    const out = prog.temperGear(g, catalysts, tome);
    if (!out) {
      run.reason = 'unaffordable';
      break;
    }
    run.attempts.push(out);
    if (out.success) {
      run.success = true;
      run.reason = 'success';
      break;
    }
  }
  return run;
}

/** Item level at each tempering stage 0..maxPlus (uses the game's own formula). */
export function temperLadder(g: GearItem): { plus: number; ilvl: number }[] {
  return Array.from({ length: g.maxPlus + 1 }, (_, plus) => ({ plus, ilvl: gearIlvl({ ...g, plus }) }));
}

// ====================================================================== sigils

export interface SigilRow {
  id: string;
  def: SigilDef;
  pts: number;
  level: number;
  books: number;
  acc: number;
  stone: number;
  chosen: boolean;
}

/** Every sigil with its point sources. Negative sigils come last. */
export function sigilRows(c: CharacterSave): SigilRow[] {
  const acc: Record<string, number> = {};
  for (const slot of ACC_SLOTS) {
    const a = c.equipment[slot];
    if (!a) continue;
    for (const s of a.sigils) acc[s.id] = (acc[s.id] ?? 0) + s.pts;
  }
  const stone: Record<string, number> = {};
  if (c.equipment.stone) for (const s of stoneSigilPoints(c.equipment.stone)) stone[s.id] = (stone[s.id] ?? 0) + s.pts;
  const rows = Object.values(SIGILS).map((def): SigilRow => {
    const books = c.sigilBooks[def.id] ?? 0;
    const pts = books + (acc[def.id] ?? 0) + (stone[def.id] ?? 0);
    return { id: def.id, def, pts, level: sigilLevel(pts), books, acc: acc[def.id] ?? 0, stone: stone[def.id] ?? 0, chosen: c.activeSigils.includes(def.id) };
  });
  return rows.sort((a, b) => Number(!!a.def.negative) - Number(!!b.def.negative) || b.pts - a.pts || a.def.name.localeCompare(b.def.name));
}

/** Silver per rune stone etch (display mirror of `Progression.etchStone`). */
export const ETCH_COST = 150;

/** Colour for a rune stone success chance (75% green → 25% red). */
export function chanceColor(chance: number): string {
  const t = Math.max(0, Math.min(1, (chance - 0.25) / 0.5));
  const hue = Math.round(4 + t * 116);
  return `hsl(${hue} 70% 58%)`;
}

/** Toggles a sigil in the chosen list; returns null when adding would exceed the limit. */
export function toggleSigil(active: readonly string[], id: string, max = 5): string[] | null {
  if (active.includes(id)) return active.filter((x) => x !== id);
  if (active.length >= max) return null;
  return [...active, id];
}

// ====================================================================== gems

export interface GemStack {
  id: string;
  type: 'dmg' | 'cd';
  level: number;
  count: number;
  def: ItemDef;
}

export function gemStacks(c: CharacterSave): GemStack[] {
  const out: GemStack[] = [];
  for (const [id, count] of Object.entries(c.inventory.stacks)) {
    const def = ITEMS[id];
    if (!def || def.kind !== 'gem' || count <= 0 || !def.level) continue;
    out.push({ id, type: def.ref === 'cd' ? 'cd' : 'dmg', level: def.level, count, def });
  }
  return out.sort((a, b) => b.level - a.level || a.type.localeCompare(b.type));
}

/** Silver cost of one fusion at a level (display mirror of `Progression.fuseGems`). */
export const fuseCost = (level: number): number => 200 * level * level;

// ====================================================================== cards

export interface CardView {
  def: CardDef;
  owned: boolean;
  awaken: number;
  dupes: number;
  /** Duplicates needed for the next awakening (0 when maxed). */
  need: number;
  canAwaken: boolean;
  deckIndex: number;
}

export function cardViews(col: CardCollection): CardView[] {
  return Object.values(CARDS)
    .map((def): CardView => {
      const lvl = col.owned[def.id];
      const owned = lvl !== undefined;
      const awaken = lvl ?? 0;
      const dupes = col.dupes[def.id] ?? 0;
      const need = owned && awaken < MAX_AWAKEN ? awakenCost(awaken) : 0;
      return { def, owned, awaken, dupes, need, canAwaken: owned && need > 0 && dupes >= need, deckIndex: col.deck.indexOf(def.id) };
    })
    .sort((a, b) => rarityIndex(b.def.rarity) - rarityIndex(a.def.rarity) || a.def.set.localeCompare(b.def.set) || a.def.name.localeCompare(b.def.name));
}

export interface SetProgress {
  id: string;
  name: string;
  pieces: number;
  awaken: number;
  bonuses: { pieces: number; awaken: number; text: string; active: boolean }[];
}

/** Card set progress for the current deck (thresholds from CARD_SETS; activation matches `cardSetBonuses`). */
export function cardSetProgress(col: CardCollection): SetProgress[] {
  const by = new Map<string, { pieces: number; awaken: number }>();
  for (const id of col.deck) {
    const d = id ? CARDS[id] : undefined;
    if (!d) continue;
    const e = by.get(d.set) ?? { pieces: 0, awaken: 0 };
    e.pieces++;
    e.awaken += col.owned[d.id] ?? 0;
    by.set(d.set, e);
  }
  return Object.values(CARD_SETS)
    .map((set): SetProgress => {
      const e = by.get(set.id) ?? { pieces: 0, awaken: 0 };
      return { id: set.id, name: set.name, pieces: e.pieces, awaken: e.awaken, bonuses: set.bonuses.map((b) => ({ ...b, active: e.pieces >= b.pieces && e.awaken >= b.awaken })) };
    })
    .sort((a, b) => b.pieces - a.pieces || a.name.localeCompare(b.name));
}

/** Initials for procedural portraits: "Captain Brenna" → "CB", "Vhal'Zeroth" → "V". */
export function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\s'-]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w && !['the', 'of', 'and'].includes(w.toLowerCase()));
  const pick = words.length > 2 ? [words[0], words[words.length - 1]] : words;
  return pick
    .map((w) => (w ?? '').charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}

// ====================================================================== misc formatting

export const fmt = (n: number): string => Math.round(n).toLocaleString('en-US');
export const fmtPct = (v: number, digits = 1): string => `${(v * 100).toFixed(digits)}%`;
export const fmtSec = (s: number): string => (s >= 10 ? `${Math.round(s)}s` : `${(Math.round(s * 10) / 10).toFixed(1)}s`);
